import { useMemo } from "react";
import { findSetting, settingsFor } from "@/admin/catalog";
import type { ScreenId, SettingEntry } from "@/admin/catalog";
import { useAppConfig, type AppConfig } from "@/api/admin/app-config";
import { useFlags, type Flag } from "@/api/admin/flags";
import { useFreeTier, type FreeTierKey } from "@/api/admin/free-tier";
import { useLLMConfig, type LLMConfig } from "@/api/admin/llm-config";

interface Base {
  key: string;
  /** Catalog entry; absent for a DB row the catalog does not know. */
  entry?: SettingEntry;
  /** Catalog entry exists but the DB has no row for it. */
  missing: boolean;
}
export type ScreenSetting =
  | (Base & { kind: "dial"; row?: AppConfig })
  | (Base & { kind: "flag"; row?: Flag })
  | (Base & { kind: "llm"; row?: LLMConfig })
  | (Base & { kind: "free_tier"; key: FreeTierKey; value?: number });

export interface ScreenSettings {
  /** Catalog entries of the screen in catalog order, then DB rows the catalog does not know. */
  settings: ScreenSetting[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/** DB rows without a catalog entry are not lost: they all surface on this screen. */
const UNCATALOGUED_SCREEN: ScreenId = "service";

export function useScreenSettings(screen: ScreenId): ScreenSettings {
  const dials = useAppConfig();
  const flags = useFlags();
  const llm = useLLMConfig();
  const free = useFreeTier();

  const settings = useMemo<ScreenSetting[]>(() => {
    const out: ScreenSetting[] = settingsFor(screen).map((entry): ScreenSetting => {
      switch (entry.kind) {
        case "dial": {
          const row = dials.data?.find((r) => r.key === entry.key);
          return { kind: "dial", key: entry.key, entry, row, missing: row === undefined };
        }
        case "flag": {
          const row = flags.data?.find((r) => r.key === entry.key);
          return { kind: "flag", key: entry.key, entry, row, missing: row === undefined };
        }
        case "llm": {
          const row = llm.data?.find((r) => r.operation === entry.key);
          return { kind: "llm", key: entry.key, entry, row, missing: row === undefined };
        }
        case "free_tier": {
          const key = entry.key as FreeTierKey;
          const value = free.data?.[key];
          return { kind: "free_tier", key, entry, value, missing: value === undefined };
        }
      }
    });
    if (screen === UNCATALOGUED_SCREEN) {
      for (const row of dials.data ?? []) {
        if (!findSetting("dial", row.key)) {
          out.push({ kind: "dial", key: row.key, row, missing: false });
        }
      }
      for (const row of flags.data ?? []) {
        if (!findSetting("flag", row.key)) {
          out.push({ kind: "flag", key: row.key, row, missing: false });
        }
      }
      for (const row of llm.data ?? []) {
        if (!findSetting("llm", row.operation)) {
          out.push({ kind: "llm", key: row.operation, row, missing: false });
        }
      }
      for (const key of Object.keys(free.data ?? {}) as FreeTierKey[]) {
        if (!findSetting("free_tier", key)) {
          out.push({ kind: "free_tier", key, value: free.data?.[key], missing: false });
        }
      }
    }
    return out;
  }, [screen, dials.data, flags.data, llm.data, free.data]);

  return {
    settings,
    isLoading: dials.isLoading || flags.isLoading || llm.isLoading || free.isLoading,
    isError: dials.isError || flags.isError || llm.isError || free.isError,
    refetch: () => {
      void dials.refetch();
      void flags.refetch();
      void llm.refetch();
      void free.refetch();
    },
  };
}
